<template>
    <div class="flex flex-col">
        <template v-if="isSetupIntent">
            <stripe-setup-form
                ref="stripeFormRef"
                :key="keyCounter"
                :payment-source-id="paymentSourceId"
                @token="paymentIntentCaptured"
                @display-stripe-error="displayStripeError"
            />
        </template>

        <stripe-payment-form
            v-else
            ref="stripeFormRef"
            :key="keyCounter"
            :amount="amount"
            :payment-source-id="paymentSourceId"
            @token="setupIntentCaptured"
            @display-stripe-error="displayStripeError"
        />

        <div class="flex justify-end">
            <prime-button
                text
                class="!p-1"
                label="Refresh Payment Form"
                @click="refreshStripeForm"
            />
        </div>
        <div
            v-if="stripeMessage.length > 0"
            class="mt-5 text-lg font-bold text-danger-400"
        >
            {{ stripeMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import StripePaymentForm from '~/components/Payments/StripePaymentForm.vue'
import StripeSetupForm from '~/components/Payments/StripeSetupForm.vue'
import type { StripeError } from '@stripe/stripe-js'

const props = defineProps<{
    paymentSourceId: string
    amount?: string
}>()

const emit = defineEmits<{
    (e: 'token', payload: string | undefined): void
}>()

const isSetupIntent = computed(() => props.amount === undefined)
const keyCounter = ref(1)
const stripeMessage = ref('')

async function paymentIntentCaptured(token: string) {
    emit('token', token)
}

async function setupIntentCaptured(token: string) {
    emit('token', token)
}

// May be used externally when payment amount changes
function refreshStripeForm() {
    keyCounter.value++
}

//--------------------------------------------
// display stripe errors
//--------------------------------------------

function displayStripeError(error: StripeError) {
    //
    // fail display error
    //
    if (error.type === 'card_error' || error.type === 'validation_error') {
        stripeMessage.value = error.message as string
        if (stripeMessage.value.length == 0) {
            stripeMessage.value = 'An unexpected error occurred.'
        }
    } else {
        stripeMessage.value = 'An unexpected error occurred.'
    }

    if (import.meta.env.DEV) {
        stripeMessage.value +=
            '          (DEV Mode: ' + error.type + ') ' + error.message
    }
    setTimeout(() => (stripeMessage.value = ''), 6000)
}

const stripeFormRef =
    ref<InstanceType<typeof StripePaymentForm | typeof StripeSetupForm>>(null)
async function submitStripeForm() {
    await stripeFormRef.value?.submitStripeElementsForm()
}
defineExpose({
    submitStripeForm,
    refreshStripeForm
})
</script>
